import { API } from "@/app/api";
import { IUserData, IUserEnumerateAll } from "@/data/api/auth-data";
import { ICoin, ICoins } from "@/data/api/main-data";
import { IUserStatsHistory } from "@/data/api/monitoring-data";
import { getNearestDayStart } from "@/data-converters/metrics-pipe";
import { getPrimaryCoinFromMap } from "@/app/shared/coin-utils";
import {
  mapApiToDomainCoins,
  mapApiToDomainCoinsWithDefaultCoin,
  mapCoinsInfo,
  mapToDomainCoinsAsIs,
  mapToDomainUserEnumerateAllAsMap,
} from "@/data/domain/mapper";
import { buildSafeHistoryInterval } from "./history-interval";
import {
  getResponseStatus,
  normalizeErrorStatus,
  postApi,
} from "./post-api";

export interface ILatestCoinSelection {
  algorithm: string;
  coin: string;
  lastShareTime: number;
}

export async function fetchAllCoins(
  coinsCallback: (
    coins: Map<string, string[]>,
    coinsAsIs: Map<string, string[]>,
    coinsInfo: Map<string, ICoin>
  ) => void,
  errorCallback: (status: string) => void
) {
  // backendQueryCoins is public and does not accept session id.
  await postApi<ICoins>(API.Query.Coins)
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        coinsCallback(
          mapApiToDomainCoinsWithDefaultCoin(response),
          mapToDomainCoinsAsIs(response),
          mapCoinsInfo(response)
        );
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchCoins(
  coinsCallback: (
    value: Map<string, string[]> | undefined,
    info: Map<string, ICoin> | undefined
  ) => void,
  selectedCoinCallback: (value: string | undefined) => void,
  errorCallback: (status: string) => void
) {
  // fetch(API.Query.Coins, { method: "POST" })
  await postApi<ICoins>(API.Query.Coins)
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const coinsMap = mapApiToDomainCoins(response);
        const selectedCoin = getPrimaryCoinFromMap(coinsMap);

        const coinsInfo = mapCoinsInfo(response);
        coinsCallback(coinsMap, coinsInfo);
        selectedCoinCallback(selectedCoin);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchCoinsAsIs(
  successCallback: (value: Map<string, string[]>) => void,
  errorCallback: (status: string) => void
) {
  // fetch(API.Query.Coins, { method: "POST" })
  await postApi<ICoins>(API.Query.Coins)
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const coinsMap = mapToDomainCoinsAsIs(response);
        successCallback(coinsMap);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchUserListAsMap(
  sessionId: string | undefined,
  coin: string | undefined,
  successCallback: (users: Map<string, IUserData>) => void,
  errorCallback: (status: string) => void
) {
  // fetch(API.User.AllUsers, {
  //   method: "POST",
  //   body: JSON.stringify({
  //     id: sessionId,
  //     size: 5000,
  //     sortBy: "averagePower",
  //     coin: coin,
  //   }),
  // })
  await postApi<IUserEnumerateAll>(API.User.AllUsers, {
    id: sessionId,
    size: 5000,
    sortBy: "averagePower",
    coin: coin,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const users = response.users ?? [];
        const usersMap: Map<string, IUserData> =
          mapToDomainUserEnumerateAllAsMap(users);
        successCallback(usersMap);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchUserIsAdmin(
  sessionId: string | undefined,
  coin: string | undefined,
  successCallback: (isAdmin: boolean) => void,
  errorCallback: (status: string) => void
) {
  if (sessionId == undefined) return;

  await postApi<IUserEnumerateAll>(API.User.AllUsers, {
    id: sessionId,
    size: 10,
    sortBy: "averagePower",
    coin: coin,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        successCallback(
          (response.users?.length != undefined && response.users?.length > 0) ??
            false
        );
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchLatestUserCoinByStatsHistory(
  sessionId: string,
  coinsMap: Map<string, string[]> | undefined,
  targetLogin?: string,
  registrationDate?: number
): Promise<ILatestCoinSelection | undefined> {
  if (!coinsMap || coinsMap.size == 0) return undefined;

  const from = registrationDate
    ? getNearestDayStart(registrationDate)
    : 0;
  const safeUserStatsInterval = buildSafeHistoryInterval({
    rawTimeFrom: from,
    groupByInterval: 86_400,
    useNextUtcDayBoundary: true,
  });

  const requests: Promise<ILatestCoinSelection | undefined>[] = [];

  for (const [algorithm] of coinsMap.entries()) {
    requests.push(
      postApi<IUserStatsHistory>(API.Query.UserStatsHistory, {
        coin: algorithm,
        id: sessionId,
        groupByInterval: safeUserStatsInterval.groupByInterval,
        timeFrom: safeUserStatsInterval.timeFrom,
        timeTo: safeUserStatsInterval.timeTo,
        targetLogin: targetLogin,
      })
        .then((response) => {
          if (!response || response.status != API.Status.Success) {
            return undefined;
          }

          let latestShareTime = 0;
          for (const stat of response.stats ?? []) {
            if (Number(stat.shareWork) == 0) continue;
            if (stat.time > latestShareTime) {
              latestShareTime = stat.time;
            }
          }

          if (latestShareTime == 0) return undefined;
          return { algorithm, coin: algorithm, lastShareTime: latestShareTime };
        })
        .catch(() => undefined)
    );
  }

  const allResults = await Promise.all(requests);

  let result: ILatestCoinSelection | undefined = undefined;
  for (const item of allResults) {
    if (!item) continue;
    if (!result || item.lastShareTime > result.lastShareTime) {
      result = item;
    }
  }

  return result;
}
